import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { BRANCHES } from '@/mocks/mockData';

interface DenemeDers {
  id: string;
  ogrenci_id: string;
  brans: string | null;
  planlanan_tarih: string | null;
  planlanan_saat: string | null;
  planlanan_sure: string | null;
  gerceklesen_sure: string | null;
  durum: string | null;
  sonuc: string | null;
  veli_memnun: boolean | null;
  ogrenci_devam_istiyor: boolean | null;
  tavsiye_edilen_haftalik_ders: number | null;
  ogretmen_degerlendirme_notu: string | null;
  ogrenci?: { ogrenci_adi: string; veli_adi: string | null; telefon: string | null };
}

interface Ogrenci {
  id: string;
  ogrenci_adi: string;
  veli_adi: string | null;
  telefon: string | null;
  branslar: string[] | null;
}

const statusColors: Record<string, string> = {
  Planlandı: 'bg-blue-100 text-blue-700',
  Yapıldı: 'bg-green-100 text-green-700',
  'Öğrenci Katılmadı': 'bg-orange-100 text-orange-700',
  Ertelendi: 'bg-yellow-100 text-yellow-700',
  İptal: 'bg-red-100 text-red-700',
};

const resultColors: Record<string, string> = {
  'Aktif Derse Döndü': 'bg-green-100 text-green-700',
  Kararsız: 'bg-yellow-100 text-yellow-700',
  'Devam Etmeyecek': 'bg-red-100 text-red-700',
  'Tekrar Aranacak': 'bg-blue-100 text-blue-700',
};

const STATUSES = ['Planlandı', 'Yapıldı', 'Öğrenci Katılmadı', 'Ertelendi', 'İptal'];
const RESULTS = ['Aktif Derse Döndü', 'Kararsız', 'Devam Etmeyecek', 'Tekrar Aranacak'];

const emptyAdd = {
  ogrenciId: '',
  brans: '',
  tarih: '',
  saat: '',
  sure: '40 dk',
  durum: 'Planlandı',
  not: '',
};

export default function DenemeDerslerimPage() {
  const { ogretmenRecord } = useAuth();
  const [data, setData] = useState<DenemeDers[]>([]);
  const [ogrenciler, setOgrenciler] = useState<Ogrenci[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [editItem, setEditItem] = useState<DenemeDers | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    if (!ogretmenRecord?.id) { setLoading(false); return; }
    const [{ data: rows }, { data: ogrs }] = await Promise.all([
      supabase
        .from('egtpanel_deneme_dersleri')
        .select('*, ogrenci:egtpanel_ogrenciler(ogrenci_adi, veli_adi, telefon)')
        .eq('ogretmen_id', ogretmenRecord.id)
        .order('planlanan_tarih', { ascending: false }),
      supabase
        .from('egtpanel_ogrenciler')
        .select('id, ogrenci_adi, veli_adi, telefon, branslar')
        .order('ogrenci_adi'),
    ]);
    setData((rows || []) as DenemeDers[]);
    setOgrenciler(ogrs || []);
    setLoading(false);
  }, [ogretmenRecord]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter(d => !filterStatus || d.durum === filterStatus);

  const selectedOgrenci = ogrenciler.find(o => o.id === addForm.ogrenciId);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ogretmenRecord?.id || !addForm.ogrenciId) return;
    setSaving(true);
    const { error } = await supabase.from('egtpanel_deneme_dersleri').insert({
      ogrenci_id: addForm.ogrenciId,
      ogretmen_id: ogretmenRecord.id,
      brans: addForm.brans || null,
      planlanan_tarih: addForm.tarih || null,
      planlanan_saat: addForm.saat || null,
      planlanan_sure: addForm.sure,
      durum: addForm.durum,
      ogretmen_degerlendirme_notu: addForm.not || null,
    });
    setSaving(false);
    if (error) { showToast('Hata: Ders eklenemedi.'); return; }
    showToast('Deneme dersi oluşturuldu!');
    setShowAdd(false);
    setAddForm(emptyAdd);
    fetchData();
  };

  const handleResultSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editItem) return;
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    await supabase.from('egtpanel_deneme_dersleri').update({
      durum: fd.get('status') as string,
      sonuc: fd.get('result') as string || null,
      gerceklesen_sure: fd.get('actualDuration') as string || null,
      ogretmen_degerlendirme_notu: fd.get('teacherNote') as string || null,
      veli_memnun: fd.get('parentSatisfied') === 'true',
      ogrenci_devam_istiyor: fd.get('studentWantsContinue') === 'true',
      tavsiye_edilen_haftalik_ders: Number(fd.get('recommended')) || null,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', editItem.id);
    setSaving(false);
    setEditItem(null);
    showToast('Sonuç kaydedildi!');
    fetchData();
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Deneme Derslerim</h1>
          <p className="text-slate-500 text-sm mt-1">Deneme derslerini planlayın ve sonuçları girin</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" /> Yeni Deneme Dersi
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam', val: data.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Bekleyen', val: data.filter(d => d.durum === 'Planlandı').length, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Yapıldı', val: data.filter(d => d.durum === 'Yapıldı').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Derse Dönen', val: data.filter(d => d.sonuc === 'Aktif Derse Döndü').length, color: 'text-emerald-700', bg: 'bg-emerald-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {['', ...STATUSES].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                filterStatus === s
                  ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {s || 'Tümü'}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-16 text-center">
          <i className="ri-calendar-todo-line text-5xl text-slate-200 block mb-3" />
          <p className="text-slate-500 font-medium">Henüz deneme dersi yok</p>
          <p className="text-slate-400 text-sm mt-1">Yukarıdaki butona tıklayarak yeni bir deneme dersi oluşturun</p>
          <button
            onClick={() => setShowAdd(true)}
            className="mt-4 inline-flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] transition-colors cursor-pointer"
          >
            <i className="ri-add-line" /> Deneme Dersi Ekle
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(d => {
            const ogrenci = d.ogrenci as { ogrenci_adi: string; veli_adi: string | null; telefon: string | null } | undefined;
            const isPending = d.durum === 'Planlandı' || d.durum === 'Ertelendi';
            return (
              <div key={d.id} className="bg-white rounded-xl border border-slate-100 p-5">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#1e3a5f] text-white font-bold text-lg shrink-0">
                      {(ogrenci?.ogrenci_adi || '?').charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-semibold text-slate-800">{ogrenci?.ogrenci_adi || '—'}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[d.durum || ''] || 'bg-slate-100 text-slate-500'}`}>{d.durum}</span>
                      </div>
                      <p className="text-sm text-slate-400">{ogrenci?.veli_adi || '—'} · {ogrenci?.telefon || '—'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-700">
                      {d.planlanan_tarih || 'Tarih yok'} {d.planlanan_saat ? `· ${d.planlanan_saat.slice(0, 5)}` : ''}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {d.brans && <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full mr-1">{d.brans}</span>}
                      {d.planlanan_sure}
                    </div>
                  </div>
                </div>

                {d.durum === 'Yapıldı' && (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-slate-400 mb-1">Veli Memnun</div>
                      <div className={`text-sm font-semibold ${d.veli_memnun ? 'text-green-600' : 'text-red-500'}`}>
                        {d.veli_memnun ? 'Evet' : 'Hayır'}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-slate-400 mb-1">Sonuç</div>
                      <div className="text-xs font-medium">
                        {d.sonuc
                          ? <span className={`px-1.5 py-0.5 rounded-full ${resultColors[d.sonuc] || 'bg-slate-100'}`}>{d.sonuc}</span>
                          : <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-slate-400 mb-1">Gerç. Süre</div>
                      <div className="text-sm font-semibold text-slate-700">{d.gerceklesen_sure || '—'}</div>
                    </div>
                  </div>
                )}

                {d.ogretmen_degerlendirme_notu && (
                  <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800 mb-3">
                    <i className="ri-sticky-note-line mr-1" /> {d.ogretmen_degerlendirme_notu}
                  </div>
                )}

                {isPending && (
                  <button
                    onClick={() => setEditItem(d)}
                    className="w-full py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1" /> Sonuç Gir
                  </button>
                )}
                {d.durum === 'Yapıldı' && (
                  <button
                    onClick={() => setEditItem(d)}
                    className="w-full py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1" /> Düzenle
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Yeni Deneme Dersi</h2>
                <p className="text-sm text-slate-400 mt-0.5">Öğrenci seçin ve dersi planlayın</p>
              </div>
              <button
                onClick={() => { setShowAdd(false); setAddForm(emptyAdd); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="p-6 space-y-4">
              {/* Öğrenci Seç */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci *</label>
                <select
                  required
                  value={addForm.ogrenciId}
                  onChange={e => {
                    const og = ogrenciler.find(o => o.id === e.target.value);
                    setAddForm(p => ({
                      ...p,
                      ogrenciId: e.target.value,
                      brans: og?.branslar?.[0] || '',
                    }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                >
                  <option value="">— Öğrenci seçiniz —</option>
                  {ogrenciler.map(o => (
                    <option key={o.id} value={o.id}>{o.ogrenci_adi}</option>
                  ))}
                </select>
              </div>

              {/* Seçilen öğrenci bilgisi */}
              {selectedOgrenci && (
                <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1e3a5f] text-white text-sm font-bold shrink-0">
                    {selectedOgrenci.ogrenci_adi.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{selectedOgrenci.ogrenci_adi}</div>
                    <div className="text-xs text-slate-400">
                      {selectedOgrenci.veli_adi || '—'} · {selectedOgrenci.telefon || '—'}
                    </div>
                  </div>
                </div>
              )}

              {/* Branş */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Branş</label>
                <div className="flex flex-wrap gap-2">
                  {BRANCHES.map(b => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setAddForm(p => ({ ...p, brans: b }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                        addForm.brans === b
                          ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/40'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tarih & Saat */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tarih</label>
                  <input
                    type="date"
                    value={addForm.tarih}
                    onChange={e => setAddForm(p => ({ ...p, tarih: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Saat</label>
                  <input
                    type="time"
                    value={addForm.saat}
                    onChange={e => setAddForm(p => ({ ...p, saat: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                </div>
              </div>

              {/* Süre */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Planlanan Süre</label>
                <div className="flex gap-2">
                  {['20 dk', '40 dk', '60 dk'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setAddForm(p => ({ ...p, sure: s }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                        addForm.sure === s
                          ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                          : 'text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Durum */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                <select
                  value={addForm.durum}
                  onChange={e => setAddForm(p => ({ ...p, durum: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                >
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>

              {/* Not */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Not (isteğe bağlı)</label>
                <textarea
                  value={addForm.not}
                  onChange={e => setAddForm(p => ({ ...p, not: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  placeholder="Öğrenci hakkında kısa bir not..."
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAdd(false); setAddForm(emptyAdd); }}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={saving || !addForm.ogrenciId}
                  className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer"
                >
                  {saving ? 'Kaydediliyor...' : 'Ders Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Result Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Deneme Sonucu Gir</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {(editItem.ogrenci as { ogrenci_adi: string } | undefined)?.ogrenci_adi} · {editItem.planlanan_tarih || '—'}
                </p>
              </div>
              <button
                onClick={() => setEditItem(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleResultSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select
                    name="status"
                    defaultValue={editItem.durum || 'Planlandı'}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  >
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Gerçekleşen Süre</label>
                  <select
                    name="actualDuration"
                    defaultValue={editItem.gerceklesen_sure || ''}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">—</option>
                    {['20 dk', '30 dk', '40 dk', '45 dk', '60 dk'].map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              {[
                { label: 'Veli memnun kaldı mı?', name: 'parentSatisfied', val: editItem.veli_memnun },
                { label: 'Devam etmek istiyor mu?', name: 'studentWantsContinue', val: editItem.ogrenci_devam_istiyor },
              ].map(f => (
                <div key={f.name} className="flex items-center justify-between">
                  <label className="text-sm text-slate-600">{f.label}</label>
                  <select
                    name={f.name}
                    defaultValue={String(f.val ?? false)}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none"
                  >
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>
              ))}

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tavsiye Edilen Haftalık Ders</label>
                <input
                  type="number"
                  name="recommended"
                  defaultValue={editItem.tavsiye_edilen_haftalik_ders || 0}
                  min={0}
                  max={7}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sonuç</label>
                <select
                  name="result"
                  defaultValue={editItem.sonuc || ''}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                >
                  <option value="">— Seçiniz —</option>
                  {RESULTS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Değerlendirme Notunuz</label>
                <textarea
                  name="teacherNote"
                  defaultValue={editItem.ogretmen_degerlendirme_notu || ''}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditItem(null)}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer disabled:opacity-60"
                >
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}